import React, { useState } from 'react';
import { SigeBlock, NotrufplanInhalt, NotrufKontakt } from './types';
import { supabase } from '../../services/supabaseClient';
import { Contact } from '../../types';
import { X, Save, Plus, Trash2, Loader2, PhoneCall, MapPin, ZoomIn, ZoomOut, AlertTriangle, BadgeCheck, ThumbsDown } from 'lucide-react';
import { Button } from '../Button';
import { buildStaticMap, KartenPunkt, MARKER_FARBE } from '../../services/osmMaps';
import { ermittleNotrufplan, kandidatZuKontakt } from './notrufAuto';
import {
  KandidatenNachKategorie, KATEGORIEN, NotfallKategorie, StellenKandidat,
  leereKandidaten, stellenSchluessel,
} from '../../services/notfallStellen';
import { merkeStelle } from '../../services/notfallStellenDb';

interface NotrufplanEditorProps {
  sigeProjektId: string;
  block: SigeBlock | null; // bestehender Notrufplan-Block oder null (wird angelegt)
  beteiligte: Contact[];
  adresse?: string;        // Baumaßnahme-Adresse für die automatische Stellen-Ermittlung
  onClose: () => void;
  onSaved: (block: SigeBlock) => void;
}

const EMPTY_KONTAKT: NotrufKontakt = { name: '', adresse: '', telefon: '' };

type KontaktMap = Record<NotfallKategorie, NotrufKontakt>;

/** Zustand der Prüf-Schaltflächen je Kategorie. */
type MerkZustand = 'ruht' | 'laeuft' | 'ok' | 'fehler';

const datumKurz = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE');
};

/**
 * Ein Kontaktblock samt Prüfung.
 *
 * Der Prüf-Teil ist der Kern der Änderung: Was die automatische Suche liefert,
 * ist ein VORSCHLAG. Erst wenn ein Mitarbeiter ihn bestätigt, wandert er mit
 * Koordinaten in die geprüften Notfallstellen und steht beim nächsten Projekt
 * in der Gegend sofort und ohne Suche bereit. „Passt nicht" ist genauso
 * wichtig: es sperrt den Fehlgriff dauerhaft und rückt den nächsten Kandidaten
 * nach — so verschwindet die zufällig nächstgelegene Kinderklinik endgültig.
 */
const KontaktFelder: React.FC<{
  kategorie: NotfallKategorie;
  titel: string;
  value: NotrufKontakt;
  kandidaten: StellenKandidat[];
  merken: MerkZustand;
  merkFehler?: string;
  onChange: (k: NotrufKontakt) => void;
  onWaehle: (kandidat: StellenKandidat) => void;
  onBestaetigen: () => void;
  onVerwerfen: () => void;
}> = ({ kategorie, titel, value, kandidaten, merken, merkFehler, onChange, onWaehle, onBestaetigen, onVerwerfen }) => {
  const inputCls = 'w-full text-sm p-2 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20';
  const hatKoordinaten = value.lat != null && value.lng != null;
  const istGeprueft = value.quelle === 'db';
  const aktuellerSchluessel = hatKoordinaten
    ? stellenSchluessel(kategorie, { osmTyp: value.osmTyp, osmId: value.osmId, name: value.name, lat: value.lat!, lng: value.lng! })
    : '';
  const alternativen = kandidaten.filter(
    k => stellenSchluessel(kategorie, k) !== aktuellerSchluessel,
  );

  return (
    <div className={`border rounded-lg p-3 space-y-2 ${istGeprueft ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-slate-600 uppercase">{titel}</div>
        {istGeprueft && (
          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
            <BadgeCheck className="w-3 h-3" />
            geprüft{value.geprueftVon ? ` · ${value.geprueftVon}` : ''}{datumKurz(value.geprueftAm) ? ` · ${datumKurz(value.geprueftAm)}` : ''}
          </span>
        )}
      </div>

      <input className={inputCls} placeholder="Name" value={value.name || ''} onChange={e => onChange({ ...value, name: e.target.value })} />
      <input className={inputCls} placeholder="Adresse" value={value.adresse || ''} onChange={e => onChange({ ...value, adresse: e.target.value })} />
      <input className={inputCls} placeholder="Telefon" value={value.telefon || ''} onChange={e => onChange({ ...value, telefon: e.target.value })} />

      {value.entfernung != null && (
        <div className="text-[11px] text-slate-400">ca. {value.entfernung} km entfernt (Luftlinie)</div>
      )}

      {value.hinweis && !istGeprueft && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>{value.hinweis}</span>
        </div>
      )}

      {alternativen.length > 0 && (
        <select
          className={`${inputCls} bg-white`}
          value=""
          onChange={e => {
            const gewaehlt = alternativen.find(k => stellenSchluessel(kategorie, k) === e.target.value);
            if (gewaehlt) onWaehle(gewaehlt);
          }}
        >
          <option value="">Andere Stelle wählen … ({alternativen.length})</option>
          {alternativen.map(k => (
            <option key={stellenSchluessel(kategorie, k)} value={stellenSchluessel(kategorie, k)}>
              {k.name} · {Math.round(k.entfernung / 100) / 10} km
              {k.quelle === 'db' ? ' · geprüft' : ''}
              {k.eignung === 'ungeeignet' ? ` · ungeeignet: ${k.hinweis}` : k.hinweis ? ` · ${k.hinweis}` : ''}
            </option>
          ))}
        </select>
      )}

      {hatKoordinaten && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button
            type="button"
            onClick={onBestaetigen}
            disabled={merken === 'laeuft'}
            className="text-[11px] font-semibold px-2 py-1 rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 flex items-center gap-1"
            title="Angaben bestätigen und für künftige Projekte merken"
          >
            {merken === 'laeuft' ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />}
            Bestätigen &amp; merken
          </button>
          <button
            type="button"
            onClick={onVerwerfen}
            disabled={merken === 'laeuft'}
            className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 disabled:opacity-40 flex items-center gap-1"
            title="Stelle passt hier nicht, nie wieder vorschlagen"
          >
            <ThumbsDown className="w-3 h-3" />
            Passt nicht
          </button>
          {merken === 'ok' && <span className="text-[11px] text-emerald-700">gespeichert</span>}
          {merken === 'fehler' && <span className="text-[11px] text-red-600">{merkFehler || 'nicht gespeichert'}</span>}
        </div>
      )}
    </div>
  );
};

export const NotrufplanEditor: React.FC<NotrufplanEditorProps> = ({
  sigeProjektId, block, beteiligte, adresse, onClose, onSaved
}) => {
  const initial: NotrufplanInhalt = (block?.inhalt && typeof block.inhalt === 'object') ? block.inhalt : {};
  const [kontakte, setKontakte] = useState<KontaktMap>({
    krankenhaus: initial.krankenhaus || EMPTY_KONTAKT,
    durchgangsarzt: initial.durchgangsarzt || EMPTY_KONTAKT,
    augenarzt: initial.augenarzt || EMPTY_KONTAKT,
    apotheke: initial.apotheke || EMPTY_KONTAKT,
  });
  const [kandidaten, setKandidaten] = useState<KandidatenNachKategorie>(leereKandidaten());
  const [merkZustand, setMerkZustand] = useState<Partial<Record<NotfallKategorie, MerkZustand>>>({});
  const [merkFehler, setMerkFehler] = useState<Partial<Record<NotfallKategorie, string>>>({});
  const [ersthelfer, setErsthelfer] = useState<{ name: string; firma?: string }[]>(initial.ersthelfer || []);
  const [kartenBild, setKartenBild] = useState<string | undefined>(initial.kartenBild);
  const [geo, setGeo] = useState<{ lat: number; lng: number } | undefined>(initial.geo);
  const [mapStellen, setMapStellen] = useState<KartenPunkt[]>([]);
  /**
   * Ersatzadresse fuer Projekte ohne eigene Baumassnahme-Adresse. Vorher war
   * der Knopf in diesem Fall schlicht aus und der Notrufplan blieb ohne Karte —
   * dabei genuegt zur Suche jede Adresse in der Naehe.
   */
  const [ersatzAdresse, setErsatzAdresse] = useState(initial.ersatzAdresse || '');
  const [mapZoom, setMapZoom] = useState<number | undefined>(undefined);
  const [zoomLaeuft, setZoomLaeuft] = useState(false);
  const [neuerName, setNeuerName] = useState('');
  const [neueFirma, setNeueFirma] = useState('');
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ermittelnLaeuft, setErmittelnLaeuft] = useState(false);
  const [ermittelnStatus, setErmittelnStatus] = useState<string | null>(null);

  const setzeKontakt = (kategorie: NotfallKategorie, k: NotrufKontakt) =>
    setKontakte(alt => ({ ...alt, [kategorie]: k }));

  // Nächstgelegene Stellen ermitteln. Der Ablauf selbst liegt in notrufAuto.ts —
  // der Assistent nutzt ihn ebenfalls; hier bleibt nur der Formularzustand.
  /** Die Adresse, auf die Suche und Karte tatsaechlich aufsetzen. */
  const suchAdresse = (adresse?.trim() || ersatzAdresse.trim());
  const nutztErsatz = !adresse?.trim() && !!ersatzAdresse.trim();

  const handleAutoErmitteln = async () => {
    if (!suchAdresse) {
      setErmittelnStatus('Keine Adresse: bitte eine Adresse in der Nähe der Baustelle eintragen.');
      return;
    }
    setErmittelnLaeuft(true);
    setErmittelnStatus('Adresse wird lokalisiert …');
    setMerkZustand({});
    setMerkFehler({});
    try {
      const ergebnis = await ermittleNotrufplan(suchAdresse, setErmittelnStatus);
      setGeo(ergebnis.geo);
      setKontakte(alt => {
        const neu = { ...alt };
        for (const k of KATEGORIEN) {
          const gefunden = ergebnis.kontakte[k];
          if (gefunden) neu[k] = gefunden;
        }
        return neu;
      });
      setKandidaten(ergebnis.kandidaten);
      setMapStellen(ergebnis.stellen);
      setMapZoom(ergebnis.zoom);
      setKartenBild(ergebnis.kartenBild);
      setErmittelnStatus(nutztErsatz
        ? `${ergebnis.status} ⚠ Karte und Entfernungen beziehen sich auf die Ersatzadresse „${ersatzAdresse.trim()}", nicht auf die Baustelle.`
        : ergebnis.status);
    } catch (e) {
      console.error('Auto-Ermittlung fehlgeschlagen', e);
      setErmittelnStatus(e instanceof Error ? e.message : 'Ermittlung fehlgeschlagen.');
    } finally {
      setErmittelnLaeuft(false);
    }
  };

  /**
   * Bestätigen: die Angaben aus dem Formular — samt der Korrekturen, die der
   * Mitarbeiter gerade von Hand vorgenommen hat — in die geprüften
   * Notfallstellen schreiben. Ab jetzt kommt die Stelle bei jedem Projekt in
   * der Gegend ohne Umkreissuche zurück.
   */
  const bestaetige = async (kategorie: NotfallKategorie) => {
    const k = kontakte[kategorie];
    if (k.lat == null || k.lng == null) {
      setMerkZustand(alt => ({ ...alt, [kategorie]: 'fehler' }));
      setMerkFehler(alt => ({ ...alt, [kategorie]: 'ohne Koordinaten nicht merkbar, bitte zuerst automatisch ermitteln' }));
      return;
    }
    setMerkZustand(alt => ({ ...alt, [kategorie]: 'laeuft' }));
    try {
      const gemerkt = await merkeStelle({
        kategorie,
        status: 'bestaetigt',
        name: k.name || '',
        adresse: k.adresse,
        telefon: k.telefon,
        lat: k.lat,
        lng: k.lng,
        osmTyp: k.osmTyp,
        osmId: k.osmId,
        hinweis: k.hinweis,
      });
      setzeKontakt(kategorie, {
        ...k,
        quelle: 'db',
        geprueftVon: gemerkt.geprueftVonName || undefined,
        geprueftAm: gemerkt.geprueftAm || undefined,
      });
      setMerkZustand(alt => ({ ...alt, [kategorie]: 'ok' }));
      setMerkFehler(alt => ({ ...alt, [kategorie]: undefined }));
    } catch (e) {
      console.error('Notfallstelle merken fehlgeschlagen', e);
      setMerkZustand(alt => ({ ...alt, [kategorie]: 'fehler' }));
      setMerkFehler(alt => ({ ...alt, [kategorie]: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' }));
    }
  };

  /**
   * Verwerfen: die Stelle als „passt hier nicht" merken, aus der Auswahlliste
   * nehmen und den nächsten Kandidaten einsetzen. Der Eintrag bleibt in der
   * Datenbank stehen — als Sperre, damit derselbe Fehlgriff nicht beim
   * nächsten Projekt zurückkommt.
   */
  const verwirf = async (kategorie: NotfallKategorie) => {
    const k = kontakte[kategorie];
    if (k.lat == null || k.lng == null) return;
    const schluessel = stellenSchluessel(kategorie, { osmTyp: k.osmTyp, osmId: k.osmId, name: k.name, lat: k.lat, lng: k.lng });
    setMerkZustand(alt => ({ ...alt, [kategorie]: 'laeuft' }));
    try {
      await merkeStelle({
        kategorie,
        status: 'verworfen',
        name: k.name || '',
        adresse: k.adresse,
        telefon: k.telefon,
        lat: k.lat,
        lng: k.lng,
        osmTyp: k.osmTyp,
        osmId: k.osmId,
        hinweis: k.hinweis,
      });
      const rest = kandidaten[kategorie].filter(c => stellenSchluessel(kategorie, c) !== schluessel);
      setKandidaten(alt => ({ ...alt, [kategorie]: rest }));
      const naechster = rest.find(c => c.eignung !== 'ungeeignet');
      setzeKontakt(kategorie, naechster ? kandidatZuKontakt(naechster) : { ...EMPTY_KONTAKT });
      setMerkZustand(alt => ({ ...alt, [kategorie]: 'ok' }));
      setMerkFehler(alt => ({ ...alt, [kategorie]: undefined }));
    } catch (e) {
      console.error('Notfallstelle verwerfen fehlgeschlagen', e);
      setMerkZustand(alt => ({ ...alt, [kategorie]: 'fehler' }));
      setMerkFehler(alt => ({ ...alt, [kategorie]: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' }));
    }
  };

  /** Aus der Auswahlliste eine andere Stelle übernehmen. */
  const waehle = (kategorie: NotfallKategorie, kandidat: StellenKandidat) => {
    setzeKontakt(kategorie, kandidatZuKontakt(kandidat));
    setMerkZustand(alt => ({ ...alt, [kategorie]: 'ruht' }));
    setMerkFehler(alt => ({ ...alt, [kategorie]: undefined }));
  };

  // Karte im Editor neu zeichnen (Zoom +/-). Nutzt die zuletzt ermittelten Punkte.
  const rebuildMap = async (z: number) => {
    if (!geo) return;
    const ziel = Math.max(3, Math.min(19, z));
    setZoomLaeuft(true);
    try {
      setKartenBild(await buildStaticMap({ ...geo, farbe: MARKER_FARBE.projekt, kuerzel: 'P' }, mapStellen, ziel));
      setMapZoom(ziel);
    } catch {
      /* Zoom-Fehler ignorieren — vorheriges Bild bleibt */
    } finally {
      setZoomLaeuft(false);
    }
  };

  const addErsthelfer = () => {
    if (!neuerName.trim()) return;
    setErsthelfer([...ersthelfer, { name: neuerName.trim(), firma: neueFirma.trim() || undefined }]);
    setNeuerName('');
    setNeueFirma('');
  };

  const addAusBeteiligten = (c: Contact) => {
    if (ersthelfer.some(e => e.name === c.name)) return;
    setErsthelfer([...ersthelfer, { name: c.name, firma: c.company || undefined }]);
  };

  const handleSave = async () => {
    setSaving(true);
    const inhalt: NotrufplanInhalt = {
      krankenhaus: kontakte.krankenhaus,
      durchgangsarzt: kontakte.durchgangsarzt,
      augenarzt: kontakte.augenarzt,
      apotheke: kontakte.apotheke,
      ersthelfer,
      kartenBild,
      geo,
      ersatzAdresse: ersatzAdresse.trim() || undefined,
    };
    try {
      if (block) {
        const { data, error } = await supabase.from('sige_bloecke')
          .update({ inhalt }).eq('id', block.id).select().single();
        if (error) throw error;
        if (data) onSaved(data as SigeBlock);
      } else {
        const { data, error } = await supabase.from('sige_bloecke')
          .insert({ sige_projekt_id: sigeProjektId, typ: 'notrufplan', titel: 'Notrufplan', inhalt })
          .select().single();
        if (error) throw error;
        if (data) onSaved(data as SigeBlock);
      }
      onClose();
    } catch (e) {
      console.error('Notrufplan speichern fehlgeschlagen', e);
      setFehler('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full text-sm p-2 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20';

  const TITEL: Record<NotfallKategorie, string> = {
    krankenhaus: 'Nächstes Krankenhaus',
    durchgangsarzt: 'Durchgangsarzt',
    augenarzt: 'Augenarzt',
    apotheke: 'Apotheke',
  };

  return (
    <div className="fixed inset-0 z-[170] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-red-600 text-white">
          <h2 className="text-lg font-bold flex items-center gap-2"><PhoneCall className="w-5 h-5" /> Notrufplan</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto space-y-6">
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-800 flex gap-6">
            <span><b>Feuerwehr / Rettungsdienst:</b> 112</span>
            <span><b>Polizei:</b> 110</span>
            <span className="text-red-500 text-xs self-center">(fest im Plan)</span>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Automatisch ermittelte Angaben sind ein <b>Vorschlag</b>. Bitte Telefonnummern und Adressen prüfen und
              mit <b>„Bestätigen &amp; merken"</b> freigeben. Bestätigte Stellen stehen bei künftigen Projekten in
              derselben Gegend sofort und ohne Suche bereit.
            </span>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50/70 border border-blue-100 rounded-xl p-4 shadow-sm space-y-3">
            <div className="flex items-center gap-4 flex-wrap justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant="ai"
                  onClick={handleAutoErmitteln}
                  disabled={ermittelnLaeuft || !suchAdresse}
                  className="relative overflow-hidden font-bold px-6 py-3 shadow-md group hover:scale-[1.03] active:scale-95 transition-all bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-700 hover:via-indigo-700 hover:to-violet-700 text-white rounded-xl border-0 ring-2 ring-indigo-500/30 hover:ring-indigo-500/60"
                >
                  {ermittelnLaeuft ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <MapPin className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                  )}
                  Stellen automatisch ermitteln
                  <span className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-cyan-300 via-violet-300 to-amber-300 animate-[pulse_2s_infinite]" />
                </Button>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-slate-700">Geprüfte Stellen, OpenStreetMap &amp; KI</span>
                  <span className="text-[11px] text-slate-500">Krankenhaus, Durchgangsarzt, Augenarzt &amp; Apotheke, bestätigte Einträge zuerst</span>
                </div>
              </div>

              {/* Pulsierendes Live-Aktivitäts-Badge if processing */}
              {ermittelnLaeuft && (
                <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 px-3 py-1 rounded-full text-xs font-semibold text-indigo-700 animate-pulse">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-600"></span>
                  </span>
                  Suche läuft...
                </div>
              )}
            </div>
            {!adresse?.trim() && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <div className="text-xs text-amber-800 font-medium">
                  Keine Baumaßnahme-Adresse hinterlegt. Für Suche und Karte genügt eine <b>Adresse in der Nähe</b>
                  {' '}(Nachbargebäude, Kreuzung, Ortsmitte). Sie wird nur zum Verorten benutzt.
                </div>
                <input
                  className="w-full text-sm border border-amber-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="z. B. Hauptstraße 1, 21079 Hamburg"
                  value={ersatzAdresse}
                  onChange={e => setErsatzAdresse(e.target.value)}
                />
                {nutztErsatz && (
                  <div className="text-[11px] text-amber-700">
                    Karte und Entfernungen beziehen sich dann auf diese Adresse; der Plan weist das unter der Karte aus.
                  </div>
                )}
              </div>
            )}
            {ermittelnStatus && (
              <div className="text-xs text-indigo-900 bg-indigo-50/50 p-2.5 rounded-lg border border-indigo-100 font-mono flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-ping mt-1.5 flex-shrink-0" />
                <span>{ermittelnStatus}</span>
              </div>
            )}
            {kartenBild && (
              <div>
                <img src={kartenBild} alt="Kartenausschnitt mit Notfallstellen" className="rounded border border-slate-200 max-h-48" />
                <div className="flex items-center gap-2 mt-1">
                  <button type="button" onClick={() => rebuildMap((mapZoom ?? 13) - 1)} disabled={zoomLaeuft || !geo}
                    className="p-1 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40" title="Verkleinern">
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => rebuildMap((mapZoom ?? 13) + 1)} disabled={zoomLaeuft || !geo}
                    className="p-1 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40" title="Vergrößern">
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  {zoomLaeuft && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                  <span className="text-[11px] text-slate-400">Zoom {mapZoom ?? 'nicht gesetzt'}</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Rot = Projekt · Blau = Krankenhaus · Grün = Augenarzt · Gelb = Apotheke · © OpenStreetMap</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {KATEGORIEN.map(kategorie => (
              <KontaktFelder
                key={kategorie}
                kategorie={kategorie}
                titel={TITEL[kategorie]}
                value={kontakte[kategorie]}
                kandidaten={kandidaten[kategorie]}
                merken={merkZustand[kategorie] || 'ruht'}
                merkFehler={merkFehler[kategorie]}
                onChange={k => setzeKontakt(kategorie, k)}
                onWaehle={k => waehle(kategorie, k)}
                onBestaetigen={() => bestaetige(kategorie)}
                onVerwerfen={() => verwirf(kategorie)}
              />
            ))}
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-600 uppercase mb-2">Ersthelfer auf der Baustelle</div>
            <div className="space-y-1 mb-3">
              {ersthelfer.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                  <span className="flex-1 font-medium">{e.name}</span>
                  <span className="text-slate-500">{e.firma}</span>
                  <button onClick={() => setErsthelfer(ersthelfer.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {ersthelfer.length === 0 && <div className="text-sm text-slate-400 italic">Noch keine Ersthelfer erfasst.</div>}
            </div>
            <div className="flex gap-2 mb-3">
              <input className={inputCls} placeholder="Name" value={neuerName} onChange={e => setNeuerName(e.target.value)} />
              <input className={inputCls} placeholder="Firma" value={neueFirma} onChange={e => setNeueFirma(e.target.value)} />
              <Button variant="outline" size="sm" onClick={addErsthelfer}><Plus className="w-4 h-4" /></Button>
            </div>

            {beteiligte.length > 0 && (
              <div>
                <div className="text-[11px] text-slate-400 mb-1">Aus Projektbeteiligten übernehmen:</div>
                <div className="flex flex-wrap gap-1">
                  {beteiligte.slice(0, 20).map(c => (
                    <button
                      key={c.id}
                      onClick={() => addAusBeteiligten(c)}
                      className="text-xs px-2 py-1 rounded-full border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 text-slate-600 transition-colors"
                    >
                      + {c.name}{c.company ? ` (${c.company})` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end items-center gap-3">
          {fehler && <span className="text-xs text-red-600 mr-auto">{fehler}</span>}
          <Button variant="ghost" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};
